// Acha telefone e link DENTRO do texto da mensagem, pra virarem toque.
//
// O risco aqui não é deixar de achar um telefone — é achar telefone onde não
// tem. Mensagem de loja de material é feita de número: código de produto
// (24434), medida (58X58), preço (311,90), quantidade (23,000), nota fiscal,
// CNPJ, CEP. Se a régua for frouxa, a conversa vira um campo de links verdes e
// ninguém confia mais em nenhum.
//
// Então a regra é conservadora de propósito, e recusa quando fica em dúvida.

// DDDs que existem no Brasil. É a peneira mais eficiente que existe aqui: sem
// ela, "1.000 2.000" numa lista de preço vira "(10) 0020-00". Números como 20,
// 23, 25, 26, 29, 30, 36, 39, 40, 50, 52, 56-60, 70, 72, 76, 78, 80 e 90 nunca
// foram distribuídos.
const DDDS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24, 27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46, 47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

// Varredura larga: pega qualquer sequência que PAREÇA telefone pra depois
// julgar direito. Barra e vírgula ficam de fora do conjunto de propósito — é o
// que faz data (28/08/2026) e preço (1.234,56) nem chegarem ao julgamento.
// Começa opcionalmente em "(" ou "+": sem isso a varredura começava no
// primeiro DÍGITO e o parêntese ficava de fora do trecho marcado — o sublinhado
// saía em "(32) 99916-1234", com o "(" solto do lado.
const CANDIDATO = /[+(]?\d[\d\s().+-]{6,18}\d/g;
const LINK = /(https?:\/\/[^\s<>"]+|www\.[^\s<>"]+)/gi;

/**
 * Valida um punhado de dígitos como telefone brasileiro.
 * `ddd`: o DDD da conversa, usado quando a pessoa escreve o número sem ele
 * ("mando pro 99916-1234") — é o mesmo contexto que ela tem na cabeça.
 * Devolve o número em formato internacional (5532999161234) ou null.
 */
export function validarTelefone(digitos, { ddd } = {}) {
  let n = String(digitos || '').replace(/\D/g, '');
  if (!n) return null;

  // Prefixo do país só vale se o que sobra for um número inteiro.
  let tinhaPais = false;
  if (n.startsWith('55') && (n.length === 12 || n.length === 13)) {
    n = n.slice(2);
    tinhaPais = true;
  }

  let area;
  let local;
  if (n.length === 10 || n.length === 11) {
    area = n.slice(0, 2);
    local = n.slice(2);
  } else if (n.length === 9 && ddd) {
    // Sem DDD escrito: só aceita CELULAR (9 dígitos começando em 9). Fixo de 8
    // dígitos sem DDD fica de fora — "3741-1234" é indistinguível de número de
    // pedido, e errar aqui é pior do que não achar.
    area = String(ddd);
    local = n;
  } else {
    return null;
  }

  if (!DDDS.has(Number(area))) return null;
  if (local.length === 9 && !local.startsWith('9')) return null;
  // Fixo: a operadora nunca abriu prefixo 0, 1, 6, 7, 8 ou 9 pra linha fixa.
  if (local.length === 8 && !/^[2-5]/.test(local)) return null;
  if (local.length !== 8 && local.length !== 9) return null;
  // Sem DDD escrito E sem prefixo de país, um fixo não passa (ver acima).
  if (local.length === 8 && !tinhaPais && n.length === 8) return null;

  return `55${area}${local}`;
}

// Tira pontuação que grudou nas pontas da varredura larga, e devolve os limites
// certos — sem isto o pedaço marcado na tela sai torto (" (32) 99916-1234.").
function aparar(texto, inicio, fim) {
  let i = inicio;
  let f = fim;
  while (i < f && !/[\d+(]/.test(texto[i])) i += 1;
  while (f > i && !/\d/.test(texto[f - 1])) f -= 1;
  return [i, f];
}

// Grudado num dígito maior é outra coisa: pedaço de CNPJ, de código, de conta.
function soltoNoTexto(texto, inicio, fim) {
  return !/\d/.test(texto[inicio - 1] || '') && !/\d/.test(texto[fim] || '');
}

/**
 * Quebra o texto em pedaços pra renderizar: `{ tipo: 'texto'|'link'|'telefone' }`.
 *
 * Link vem PRIMEIRO e os telefones só são procurados no que sobra. Sem essa
 * ordem, os dígitos de dentro de uma URL (id de pedido, código de rastreio)
 * viravam telefone e o link quebrava no meio.
 */
export function partirTexto(texto, { ddd, ignorar, comTelefone = true } = {}) {
  const s = String(texto || '');
  if (!s) return [];

  const pedacos = [];
  let cursor = 0;
  LINK.lastIndex = 0;
  let m = LINK.exec(s);
  while (m) {
    if (m.index > cursor) pedacos.push({ tipo: 'bruto', valor: s.slice(cursor, m.index) });
    pedacos.push({ tipo: 'link', valor: m[0] });
    cursor = m.index + m[0].length;
    m = LINK.exec(s);
  }
  if (cursor < s.length) pedacos.push({ tipo: 'bruto', valor: s.slice(cursor) });

  const fora = [];
  pedacos.forEach((p) => {
    if (p.tipo !== 'bruto') { fora.push(p); return; }
    if (!comTelefone) { fora.push({ tipo: 'texto', valor: p.valor }); return; }
    fora.push(...acharTelefones(p.valor, { ddd, ignorar }));
  });
  return fora.filter((p) => p.valor !== '');
}

function acharTelefones(trecho, { ddd, ignorar }) {
  const saida = [];
  let cursor = 0;
  CANDIDATO.lastIndex = 0;
  let m = CANDIDATO.exec(trecho);
  while (m) {
    const [ini, fim] = aparar(trecho, m.index, m.index + m[0].length);
    const bruto = trecho.slice(ini, fim);
    const numero = soltoNoTexto(trecho, ini, fim) ? validarTelefone(bruto, { ddd }) : null;
    // O número da própria conversa não vira toque: oferecer "conversar" com
    // quem já está na tela não leva a lugar nenhum.
    const vale = numero && numero !== String(ignorar || '').replace(/\D/g, '');
    if (vale) {
      if (ini > cursor) saida.push({ tipo: 'texto', valor: trecho.slice(cursor, ini) });
      saida.push({ tipo: 'telefone', valor: bruto, numero });
      cursor = fim;
    }
    m = CANDIDATO.exec(trecho);
  }
  if (cursor < trecho.length) saida.push({ tipo: 'texto', valor: trecho.slice(cursor) });
  return saida;
}

/** (32) 99916-1234 — como a pessoa lê, não como o banco guarda. */
export function formatarTelefone(numero) {
  const n = String(numero || '').replace(/\D/g, '');
  const local = n.startsWith('55') ? n.slice(2) : n;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return numero;
}

/** DDD de um número da conversa, pra servir de contexto ao que vem sem DDD. */
export function dddDe(numero) {
  const n = String(numero || '').replace(/\D/g, '');
  const local = n.startsWith('55') ? n.slice(2) : n;
  return (local.length === 10 || local.length === 11) ? local.slice(0, 2) : null;
}
